import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Play, ArrowUpRight } from "lucide-react";
import { talk } from "@/data/portfolio";

/**
 * The TEDx talk, as a click-to-load facade.
 *
 * Nothing is requested from a third party until the visitor presses play:
 * the poster is a local file and the iframe is only mounted on click. That
 * keeps the ~1MB of YouTube player JS, and its cookies, off the initial load.
 *
 * The talk title is quoted verbatim from the TEDx Talks channel. It is a
 * citation, so it keeps its own punctuation.
 */
export const TedxTalk = () => {
  const [playing, setPlaying] = useState(false);
  const frameRef = useRef(null);

  // Move focus into the player once it mounts, so keyboard users land on it.
  useEffect(() => {
    if (playing && frameRef.current) frameRef.current.focus();
  }, [playing]);

  return (
    <section
      id="talk"
      className="relative bg-ink text-cream py-24 md:py-32 overflow-hidden"
      data-testid="tedx-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
          className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-12 md:mb-16"
        >
          <h2 className="col-span-12 md:col-span-7 font-display text-4xl md:text-6xl leading-[1] tracking-tight">
            Why I built a payroll.
          </h2>
          <div className="col-span-12 md:col-span-4 md:col-start-9 md:text-right space-y-1">
            <p className="label text-gold">
              On the TEDx stage
            </p>
            <p className="label text-cream/60">
              {talk.event} · {talk.date}
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-12 gap-8 md:gap-12">
          {/* Player */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-8% 0px" }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="col-span-12 lg:col-span-8"
          >
            <div className="relative w-full aspect-video bg-forest overflow-hidden">
              {playing ? (
                <iframe
                  ref={frameRef}
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube-nocookie.com/embed/${talk.videoId}?autoplay=1&rel=0&modestbranding=1`}
                  title={talk.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  data-testid="tedx-iframe"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  data-testid="tedx-play"
                  data-cursor="hover"
                  aria-label={`Play the talk: ${talk.title}`}
                  className="group absolute inset-0 w-full h-full cursor-pointer"
                >
                  <img
                    src={talk.poster}
                    alt="Shruthi Ravi speaking at TEDxGlobalAcademy"
                    width={1280}
                    height={720}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.03]"
                  />
                  <span className="absolute inset-0 bg-ink/25 group-hover:bg-ink/10 transition-colors duration-500" />
                  <span className="absolute inset-0 grid place-items-center">
                    <span className="flex items-center gap-4 px-7 py-4 bg-cream/95 text-ink group-hover:bg-terracotta group-hover:text-cream transition-colors duration-500">
                      <Play size={15} className="shrink-0" />
                      <span className="label">
                        Watch the talk
                      </span>
                      <span className="label normal-case text-ink2 group-hover:text-cream/60">
                        {talk.duration}
                      </span>
                    </span>
                  </span>
                </button>
              )}
            </div>

            <p className="mt-4 label text-cream/55">
              {playing ? "Now playing" : "Plays here · nothing loads until you press play"}
            </p>
          </motion.div>

          {/* Framing */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-8% 0px" }}
            transition={{ duration: 1, delay: 0.08, ease: [0.19, 1, 0.22, 1] }}
            className="col-span-12 lg:col-span-4 flex flex-col justify-between"
          >
            <div>
              <p className="font-display text-2xl md:text-3xl leading-[1.25] text-cream">
                The talk is called &ldquo;{talk.citedTitle}&rdquo;.
              </p>
              <p className="mt-6 font-sans font-light text-base md:text-lg leading-relaxed text-cream/70">
                It is the story of the women who were making beautiful things out
                of newspaper and were not being paid what the work was worth, and
                why I answered it with a building rather than a donation.
              </p>

              <dl className="mt-10 border-t border-cream/15 pt-5 space-y-4">
                <div className="flex justify-between gap-6">
                  <dt className="label text-cream/55">
                    Event
                  </dt>
                  <dd className="font-sans text-sm text-cream text-right">
                    {talk.event}
                  </dd>
                </div>
                <div className="flex justify-between gap-6">
                  <dt className="label text-cream/55">
                    Channel
                  </dt>
                  <dd className="font-sans text-sm text-cream text-right">
                    {talk.channel}
                  </dd>
                </div>
                <div className="flex justify-between gap-6">
                  <dt className="label text-cream/55">
                    Subject
                  </dt>
                  <dd className="font-sans text-sm text-cream text-right">
                    The Teal Door Cafe
                  </dd>
                </div>
              </dl>
            </div>

            <div className="mt-10 flex flex-col gap-3">
              <Link
                to="/contact"
                data-testid="tedx-speaking-cta"
                data-cursor="hover"
                className="group inline-flex items-center justify-between gap-3 px-6 py-4 bg-gold text-ink label hover:bg-cream transition-colors duration-500"
              >
                Invite me to speak
                <ArrowUpRight
                  size={14}
                  className="transition-transform group-hover:rotate-45"
                />
              </Link>
              <a
                href={talk.url}
                target="_blank"
                rel="noopener"
                data-testid="tedx-external"
                data-cursor="hover"
                className="inline-flex items-center gap-2 label text-cream/60 hover:text-cream transition-colors"
              >
                Watch on YouTube <ArrowUpRight size={13} />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
